<script setup lang="ts">
import RevisionGatedButton from "@/pages/ProjectVersionHistory/components/revisions/RevisionGatedButton.vue";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import { ProjectTransportMappingEmissionFactor_Arr_Rel_Insert_Input, TransportSourceFragmentFragment } from "@/graphql";
import format from "@/services/Utils/format";
import { computed, ref, watch } from "vue";
import useTransportMappingList, { InsertProjectTransportMappingInput } from "../../providers/TransportMappingListProvider";
import calculateStandardValueEmissions from "../../utils/calculateStandardValueEmissions";
import { emissionPerInputUnit, inputValueForTotal, isKilometerFactor } from "../../utils/transportEmissionFactorUnit";
import TransportEmissionFactorInput from "./TransportEmissionFactorInput.vue";
import TransportEmissionFactorSelect from "./TransportEmissionFactorSelect.vue";
import TransportInputLabel from "./TransportInputLabel.vue";
import TransportSourceSelect from "./TransportSourceSelect.vue";
import TransportStandardValueSelect from "./TransportStandardValueSelect.vue";
import { useProjectEditReadOnly } from "@/pages/ProjectVersionHistory/composables/useProjectEditReadOnly.composable";

const props = defineProps<{
  calculationTypeId?: number;
  considerationPeriod: number;
  materialId?: string;
  sources: TransportSourceFragmentFragment[];
  totalArea: number;
  weight?: number;
}>();

const emit = defineEmits<{
  (e: "submit", payload: InsertProjectTransportMappingInput): void;
}>();

type TransportEmissionFactor = TransportSourceFragmentFragment["transportEmissionFactors"][0];

const selectedTransportSourceId = ref<string | null>(null);
const emissionFactorInputValues = ref<Record<string, string | null>>({});
// Saved as the mapping's distance, so the API can redo km × weight × emission when the weight changes.
const kilometers = ref<number>(0);
const selectedTransportEmissionFactorId = ref<string | null>(null);
const selectedStandardValueId = ref<string | null>(null);
const emission = ref<{ scaled: number; total: number } | null>(null);
const transportMappingList = useTransportMappingList();

const considerationPeriod = computed(() => {
  return props.considerationPeriod;
});

const totalArea = computed(() => {
  return props.totalArea;
});

const selectedStandardValue = computed(() => {
  return selectedTransportSource.value?.transportStandardValues.find((x) => x.id === selectedStandardValueId.value);
});

const selectedStandardValueUnit = computed(() => {
  const calculationType = selectedTransportSource.value?.calculationTypes.find(
    (x) => x.calculationType.id === props.calculationTypeId
  )?.calculationType;

  const denominatorLabel = selectedStandardValue.value?.denominatorUnit?.label;
  const numeratorLabel = calculationType?.thresholdIndicator.indicatorType.unit;

  return denominatorLabel ? [numeratorLabel, denominatorLabel].join("/") : numeratorLabel;
});

const selectedTransportSource = computed(() => {
  return props.sources.find((source) => source.id === selectedTransportSourceId.value);
});

const transportEmissionFactors = computed(() => {
  const emissionFactors = selectedTransportSource.value?.transportEmissionFactors || [];

  if (!selectedTransportEmissionFactorId.value) return [];

  const item = emissionFactors.find((x) => x.id === selectedTransportEmissionFactorId.value);
  if (!item) return [];

  return emissionFactors.filter((x) => x.label === item.label && x.transportSourceId === item.transportSourceId);
});

const transportStandardValues = computed(() => {
  return selectedTransportSource.value?.transportStandardValues || [];
});

const isValid = computed(() => {
  return selectedStandardValueId.value || Object.keys(emissionFactorInputValues.value).length > 0;
});

// Source, emission factor and the calculated totals stay explorable while a frozen revision is
// previewed; only the Add stays shut, because the mapping would be stamped onto the CURRENT
// revision rather than the one on screen (RTLCA-5254).
//
// A Viewer is a different case and locks the selects too (RTLCA-5581): switching revision would not
// give them the permission, so there is nothing for them to explore towards. `lacksEditPermission`
// is the role alone, which is why the preview keeps its browsable form.
const { blockedByPreview, lacksEditPermission } = useProjectEditReadOnly();

const handleEmissionFactorChange = (factor: TransportEmissionFactor, value: number | undefined) => {
  emissionFactorInputValues.value[factor.id] = (value || 0) + "";
  kilometers.value = isKilometerFactor(factor) ? value || 0 : 0;

  const emissionFactorEmission = (value || 0) * emissionPerInputUnit(factor, props.weight || 0);
  const scaled = emissionFactorEmission / totalArea.value / considerationPeriod.value;
  emission.value = {
    scaled: scaled === Infinity ? 0 : scaled,
    total: emissionFactorEmission,
  };

  // Calculate the emission factor for all other emission factors
  transportEmissionFactors.value.forEach((otherFactor) => {
    if (otherFactor.id !== factor.id) {
      const otherValue = inputValueForTotal(emissionFactorEmission, otherFactor, props.weight || 0);
      emissionFactorInputValues.value[otherFactor.id] = format.decimalNumber(otherValue);
      if (isKilometerFactor(otherFactor)) kilometers.value = otherValue;
    }
  });
};

const handleStandardValueChange = (standardValueId: string | null) => {
  selectedStandardValueId.value = standardValueId;

  const item = transportStandardValues.value.find((x) => x.id === standardValueId);

  if (!item) {
    emission.value = null;
    return;
  }

  const totalEmission = item.emission * (props.weight || 0);

  emission.value = {
    scaled: totalEmission / totalArea.value / considerationPeriod.value,
    total: totalEmission,
  };

  emission.value = calculateStandardValueEmissions(item.emission, props.weight || 0, considerationPeriod.value, totalArea.value);
};

async function handleSubmit() {
  // Guarded as well as disabled: the revision can change while this row sits expanded.
  if (blockedByPreview()) return;

  const emissionFactors: ProjectTransportMappingEmissionFactor_Arr_Rel_Insert_Input["data"] = Object.keys(
    emissionFactorInputValues.value
  ).map((transportEmissionFactorId) => ({
    transportEmissionFactorId,
  }));

  const payload: InsertProjectTransportMappingInput = {
    distance: selectedStandardValueId.value ? 0 : kilometers.value,
    emissionFactors,
    materialId: props.materialId,
    transportEmissionFactorEmission:
      Object.keys(emissionFactorInputValues.value).length > 0 && !selectedStandardValueId.value
        ? emission.value?.total
        : undefined,
    transportStandardValueId: selectedStandardValueId.value || undefined,
  };

  emit("submit", payload);

  // Clears via the watch below, which cascades into the factor/standard-value/amount fields.
  selectedTransportSourceId.value = null;
}

watch(selectedTransportSource, () => {
  emission.value = null;
  emissionFactorInputValues.value = {};
  kilometers.value = 0;
  selectedTransportEmissionFactorId.value = null;
  selectedStandardValueId.value = null;
});

// A value typed for the previous factor would otherwise be submitted with it.
watch(selectedTransportEmissionFactorId, () => {
  if (selectedStandardValueId.value) return;
  emission.value = null;
  emissionFactorInputValues.value = {};
  kilometers.value = 0;
});
</script>

<template>
  <div class="col-span-2">
    <TransportInputLabel
      class="pb-1"
      primary-text="Data source" />
    <TransportSourceSelect
      v-model="selectedTransportSourceId"
      :disabled="lacksEditPermission"
      :options="sources" />
  </div>
  <div
    v-if="(selectedTransportSource?.transportEmissionFactors.length || 0) > 0"
    class="col-span-3">
    <TransportInputLabel primary-text="Emission factor" />
    <TransportEmissionFactorSelect
      v-model="selectedTransportEmissionFactorId"
      :disabled="lacksEditPermission"
      :options="selectedTransportSource?.transportEmissionFactors || []" />
  </div>

  <template v-if="transportStandardValues.length > 0">
    <TransportStandardValueSelect
      :disabled="lacksEditPermission"
      :options="transportStandardValues"
      @change="handleStandardValueChange" />
  </template>

  <div
    v-for="emissionFactor in transportEmissionFactors"
    v-if="transportEmissionFactors.length > 0"
    :key="emissionFactor.id">
    <TransportInputLabel
      :primary-text="emissionFactor.denominatorUnit.label"
      :secondary-text="`(${emissionFactor.denominatorUnit.unit})`" />

    <TransportEmissionFactorInput
      :disabled="lacksEditPermission"
      :emission-factor="emissionFactor"
      :value="emissionFactorInputValues[emissionFactor.id] || ''"
      @change="(value: number) => handleEmissionFactorChange(emissionFactor, value)" />
  </div>

  <div v-if="selectedStandardValue && selectedStandardValueUnit">
    <TransportInputLabel :primary-text="selectedStandardValueUnit" />
    <NumberInput
      align-right
      class="text-slate-600"
      color="primary"
      disabled
      positive
      :value="format.decimalNumber(selectedStandardValue.emission || 0)" />
  </div>

  <template v-if="transportEmissionFactors.length > 0 || selectedStandardValueId">
    <div>
      <TransportInputLabel primary-text="Total emission" />
      <NumberInput
        align-right
        class="text-slate-600"
        color="primary"
        disabled
        positive
        :value="format.decimalNumber(emission?.total || 0)" />
    </div>
    <div>
      <TransportInputLabel primary-text="Total scaled emission" />
      <NumberInput
        align-right
        class="text-slate-600"
        color="primary"
        disabled
        positive
        :value="format.decimalNumber(emission?.scaled || 0)" />
    </div>
    <div class="mt-auto flex justify-center">
      <RevisionGatedButton
        :disabled="!isValid"
        :guard="useProjectEditReadOnly"
        :is-loading="transportMappingList.getLoadingUi()"
        :color="isValid ? 'primary' : 'disabled'"
        label="Add"
        @click="handleSubmit" />
    </div>
  </template>
</template>
